/**
 * Status chips.
 *
 * ProvenanceBadge is the important one. It appears wherever a metric appears, so a
 * reader can never mistake something the vendor asserted for something the platform
 * measured. A dashed outline means unverified; a solid fill means the platform read the
 * underlying records itself.
 */

import { PROVENANCE_LABEL, isVerified, severityColor, type Severity } from "../lib/format";

const chip: React.CSSProperties = {
  fontSize: 10.5,
  letterSpacing: "0.04em",
  padding: "2px 8px",
  borderRadius: 10,
  whiteSpace: "nowrap",
  display: "inline-block",
};

export function ProvenanceBadge({ provenance }: { provenance: string }) {
  const verified = isVerified(provenance);
  return (
    <span
      style={{
        ...chip,
        color: verified ? "var(--parchment)" : "var(--slate)",
        border: verified ? "1px solid var(--line-strong)" : "1px dashed var(--line-strong)",
        background: verified ? "rgba(239,233,221,0.08)" : "transparent",
      }}
      title={
        verified
          ? "Computed by Actuovious from records in a system the client controls."
          : "Asserted by the vendor. No independent source, so no verified claim rests on it."
      }
    >
      {PROVENANCE_LABEL[provenance] ?? provenance}
    </span>
  );
}

export function SeverityBadge({ severity }: { severity: Severity }) {
  if (severity === "none") return null;
  const colour = severityColor(severity);
  const text = severity === "high" ? "Material gap" : severity === "med" ? "Gap" : "Aligned";
  return (
    <span style={{ ...chip, color: colour, border: `1px solid ${colour}`, background: "transparent" }}>
      {text}
    </span>
  );
}

export function TierBadge({ tier }: { tier: string }) {
  const colour =
    tier === "Strategic" ? "var(--gold)" : tier === "Core" ? "var(--parchment)" : "var(--slate)";
  return (
    <span style={{ ...chip, color: colour, border: `1px solid ${colour}`, opacity: 0.9 }}>
      {tier}
    </span>
  );
}

export function TargetBadge({ met }: { met: boolean | null }) {
  if (met === null) return <span style={{ ...chip, color: "var(--slate)" }}>Not measured</span>;
  const colour = met ? "var(--green)" : "var(--rust)";
  return (
    <span style={{ ...chip, color: colour, border: `1px solid ${colour}` }}>
      {met ? "Met" : "Missed"}
    </span>
  );
}

export function VerdictBadge({ verdict }: { verdict: string }) {
  if (verdict !== "insufficient_evidence") return null;
  return (
    <span
      style={{
        ...chip,
        color: "var(--slate)",
        border: "1px dashed var(--line-strong)",
        background: "transparent",
      }}
      title="The evidence did not cover the window, so no independent figure was produced. This is not the same as the supplier missing the target."
    >
      Evidence incomplete
    </span>
  );
}

export function SourceBadge({ source }: { source: string }) {
  if (source !== "agreed-rule") return null;
  return (
    <span
      style={{ ...chip, color: "var(--slate)", border: "1px dotted var(--line-strong)" }}
      title="A standard both parties operate to that no executed document states. Worth measuring and worth raising; not a contractual credit."
    >
      Agreed rule
    </span>
  );
}
